import { DEMO_RUN } from "@/config";
import { API_BASE_URL } from "@/config/api";
import { getJsonAuthHeaders, getJsonCsrfHeaders } from "@/lib/apiHeaders";
import { storage } from "@/services/storage";
import type { User, UserCredential } from "@/types/auth";
import { NewUser } from "@/types/auth";
import { reportService } from "./reportService";
// Disable local fallback regardless of config setting
const ENABLE_LOCAL_FALLBACK = false;

// Function to decode JWT token
function decodeJwtToken(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT token:", error);
    return null;
  }
}

// Demo users data
const demoUsers: UserCredential[] = [
  {
    id: "123456789",
    name: "Admin User",
    email: "admin@example.com",
    password: "admin123",
    picture:
      "https://ui-avatars.com/api/?name=Admin+User&background=0D8ABC&color=fff",
    role: "admin",
  },
  {
    id: "987654321",
    name: "Regular User",
    email: "user@example.com",
    password: "user123",
    picture:
      "https://ui-avatars.com/api/?name=Regular+User&background=ABC123&color=fff",
    role: "user",
  },
  {
    id: "111222333",
    name: "Jennifer Smith",
    email: "jennifer@example.com",
    password: "password123",
    picture:
      "https://ui-avatars.com/api/?name=Jennifer+Smith&background=FF5A5F&color=fff",
    role: "user",
  },
  {
    id: "444555666",
    name: "Michael Johnson",
    email: "michael@example.com",
    password: "password123",
    picture:
      "https://ui-avatars.com/api/?name=Michael+Johnson&background=00A699&color=fff",
    role: "user",
  },
  {
    id: "777888999",
    name: "Sarah Williams",
    email: "sarah@example.com",
    password: "password123",
    picture:
      "https://ui-avatars.com/api/?name=Sarah+Williams&background=7B0051&color=fff",
    role: "user",
  },
  // Additional demo users
  {
    id: "123123123",
    name: "David Chen",
    email: "david@example.com",
    password: "password123",
    picture:
      "https://ui-avatars.com/api/?name=David+Chen&background=5271FF&color=fff",
    role: "user",
  },
  {
    id: "456456456",
    name: "Emily Rodriguez",
    email: "emily@example.com",
    password: "password123",
    picture:
      "https://ui-avatars.com/api/?name=Emily+Rodriguez&background=FFB600&color=fff",
    role: "user",
  },
  {
    id: "789789789",
    name: "Robert Kim",
    email: "robert@example.com",
    password: "password123",
    picture:
      "https://ui-avatars.com/api/?name=Robert+Kim&background=FF4500&color=fff",
    role: "user",
  },
];

// Storage key for user data
const storageKey = "auth_user";

// Helper function to notify backend about user login
const notifyBackendLogin = async (user: User): Promise<void> => {
  try {
    console.log("Notifying backend about user login:", user.email);
    await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        ...getJsonCsrfHeaders(),
        Authorization: `Bearer header.${btoa(JSON.stringify({ email: user.email }))}.signature`,
      },
      body: JSON.stringify(user),
    });
  } catch (error) {
    // Log the error but don't throw it since this is just a notification
    console.warn("Failed to notify backend about user login:", error);
    // Continue with login process even if backend notification fails
  }
};

// Helper function to notify backend about user logout
const notifyBackendLogout = async (email: string): Promise<void> => {
  try {
    console.log("Notifying backend about user logout:", email);
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      headers: getJsonCsrfHeaders(),
      body: JSON.stringify({ email }),
    });
  } catch (error) {
    console.warn("Failed to notify backend about user logout:", error);
    // Continue with logout process even if backend notification fails
  }
};

// Get current user from storage
const getCurrentUser = () => {
  try {
    const user = storage.getItem("auth_user");
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error("Error getting current user:", error);
    return null;
  }
};

// Function to store pending role changes for later sync
const storePendingRoleChange = (userId: string, role: "user" | "admin") => {
  try {
    // Get existing pending changes
    const pendingChangesStr =
      localStorage.getItem("pending_role_changes") || "[]";
    const pendingChanges = JSON.parse(pendingChangesStr);

    // Add or update this change
    const existingIndex = pendingChanges.findIndex(
      (change) => change.userId === userId,
    );
    if (existingIndex >= 0) {
      pendingChanges[existingIndex].role = role;
      pendingChanges[existingIndex].timestamp = new Date().toISOString();
    } else {
      pendingChanges.push({
        userId,
        role,
        timestamp: new Date().toISOString(),
      });
    }

    // Save back to localStorage
    localStorage.setItem(
      "pending_role_changes",
      JSON.stringify(pendingChanges),
    );
    console.log("Stored pending role change for later sync:", { userId, role });
  } catch (error) {
    console.error("Error storing pending role change:", error);
  }
};

// Helper function to create authentication headers for API requests (with CSRF protection)
const getAuthHeaders = (): Record<string, string> => {
  const headers = getJsonAuthHeaders();
  // JWT tokens are now stored in httpOnly cookies, not localStorage
  console.log("getAuthHeaders: Using httpOnly cookie-based authentication");
  return headers;
};

export const setCurrentUser = (user: any) => {
  try {
    storage.setItem("auth_user", JSON.stringify(user));
  } catch (error) {
    console.error("Error setting current user:", error);
  }
};

export const clearCurrentUser = () => {
  try {
    storage.removeItem("auth_user");
  } catch (error) {
    console.error("Error clearing current user:", error);
  }
};

export const getUsers = () => {
  try {
    const users = storage.getItem("auth_users");
    return users ? JSON.parse(users) : [];
  } catch (error) {
    console.error("Error getting users:", error);
    return [];
  }
};

export const setUsers = (users: any[]) => {
  try {
    storage.setItem("auth_users", JSON.stringify(users));
  } catch (error) {
    console.error("Error setting users:", error);
  }
};

export const getCredentials = () => {
  // NEVER store credentials client-side for security reasons
  // Authentication uses OAuth only, no credential storage needed
  return null;
};

export const setCredentials = (credentials: any) => {
  // NEVER store credentials client-side for security reasons
  // This function is deprecated - credentials should not be stored
  console.warn(
    "setCredentials: Credentials should never be stored client-side",
  );
};

export const clearCredentials = () => {
  // Clear any legacy credential storage (should not exist)
  try {
    storage.removeItem("auth_credentials");
  } catch (error) {
    console.error("Error clearing credentials:", error);
  }
};

export const getPendingRoleChanges = () => {
  const pendingChangesStr = storage.getItem("pending_role_changes") || "[]";
  return JSON.parse(pendingChangesStr);
};

export const setPendingRoleChanges = (changes: any[]) => {
  storage.setItem("pending_role_changes", JSON.stringify(changes));
};

export const saveUser = (user: any) => {
  storage.setItem("auth_user", JSON.stringify(user));
};

export const removeUser = () => {
  storage.removeItem("auth_user");
};

export const updateUser = (user: any) => {
  storage.setItem("auth_user", JSON.stringify(user));
};

export const getPendingChanges = () => {
  const pendingChangesStr = storage.getItem("pending_role_changes");
  return pendingChangesStr ? JSON.parse(pendingChangesStr) : [];
};

export const updatePendingChanges = (changes: any[]) => {
  storage.setItem("pending_role_changes", JSON.stringify(changes));
};

export const authService = {
  // Initialize auth data in storage
  initialize: () => {
    console.log("authService.initialize - Starting initialization");

    // Check if users already exist in storage
    const storedUsers = storage.getItem("auth_users");
    // console.log('authService.initialize - Stored users:', storedUsers);

    if (!storedUsers && DEMO_RUN) {
      // console.log('authService.initialize - No users found and DEMO_RUN is true, initializing demo users');
      // Initialize with demo users if no users exist and DEMO_RUN is true
      const initialUsers = demoUsers.map(({ password, ...user }) => user);
      storage.setItem("auth_users", JSON.stringify(initialUsers));

      // Also save credentials for demo logins
      storage.setItem("auth_credentials", JSON.stringify(demoUsers));

      // Generate demo reports
      reportService.generateDemoReports();
    }

    // Return the current user from storage if available
    const storedUser = storage.getItem("auth_user");
    console.log(
      "authService.initialize - Current user from storage:",
      storedUser,
    );

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log(
          "authService.initialize - Returning parsed user:",
          parsedUser,
        );
        return parsedUser;
      } catch (e) {
        console.error("authService.initialize - Error parsing stored user:", e);
        storage.removeItem("auth_user");
        return null;
      }
    }

    console.log("authService.initialize - No current user found");
    return null;
  },

  // Get all users
  getUsers: async (): Promise<User[]> => {
    try {
      // Attempt to fetch users from API
      console.log("Fetching users from API");
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const users = await response.json();
      console.log(`Fetched ${users.length} users from API`);

      // No localStorage update when fallback is disabled
      return users;
    } catch (error) {
      console.error("Error fetching users from API:", error);
      // No localStorage fallback, just return empty array
      return [];
    }
  },

  // Get demo users for login shortcuts
  getDemoUsers: () => {
    const storedCredentials = storage.getItem("auth_credentials");
    if (storedCredentials) {
      return JSON.parse(storedCredentials).map((user: UserCredential) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        picture: user.picture,
      }));
    }
    return [];
  },

  // Login with email and password
  signInWithCredentials: async (
    email: string,
    password: string,
  ): Promise<User> => {
    // Get credentials from storage
    const storedCredentials = storage.getItem("auth_credentials");
    const credentials = storedCredentials
      ? JSON.parse(storedCredentials)
      : demoUsers;

    // Find matching user
    const matchedUser = credentials.find(
      (u: UserCredential) => u.email === email && u.password === password,
    );

    if (matchedUser) {
      const { password, ...userWithoutPassword } = matchedUser;
      const user = {
        ...userWithoutPassword,
        lastLogin: new Date().toISOString(),
      };

      // Save to localStorage
      storage.setItem("auth_user", JSON.stringify(user));
      localStorage.setItem(storageKey, JSON.stringify(user));

      // Notify backend
      await notifyBackendLogin(user);

      return user;
    } else {
      throw new Error("Invalid email or password");
    }
  },

  // Simulate Google sign in
  signInWithGoogle: async (credential?: string): Promise<User> => {
    console.log(
      "authService.signInWithGoogle - Starting with credential:",
      credential ? "Provided" : "Not provided",
    );
    // Admin role is now determined by backend user.role only

    // If we have a credential, decode it and use the information
    if (credential) {
      try {
        // Check if this is an auth code or an ID token
        if (credential.includes(".") && credential.split(".").length === 3) {
          // Looks like a JWT token (ID token)
          const decodedToken = decodeJwtToken(credential);
          console.log("Decoded Google token:", decodedToken);

          if (decodedToken) {
            const { email, name, picture, sub } = decodedToken;
            console.log("Processing Google user with email:", email);
            return authService.processGoogleUser(email, name, picture, sub);
          }
        } else {
          // Probably an auth code or some other format
          console.log(
            "Non-JWT credential received, using demo user for authentication",
          );
          // In a real app, this should exchange the auth code for tokens
          // For demo, use a default admin user
          return authService.processGoogleUser(
            "google@example.com",
            "Google User",
            "https://ui-avatars.com/api/?name=Google+User&background=random",
            "google_user_id",
          );
        }
      } catch (error) {
        console.error("Error processing Google credential:", error);
      }
    }

    // Fallback or demo mode
    if (DEMO_RUN) {
      console.log("Using demo admin account for Google sign in");
      try {
        // Use admin account for demo
        const users = await authService.getUsers();
        const adminUser = users.find((u) => u && u.role === "admin");

        if (adminUser) {
          const user = {
            ...adminUser,
            lastLogin: new Date().toISOString(),
          };

          storage.setItem("auth_user", JSON.stringify(user));
          localStorage.setItem(storageKey, JSON.stringify(user));

          // Notify backend
          await notifyBackendLogin(user);

          return user;
        }
      } catch (error) {
        console.error("Error finding admin user:", error);
      }
    }

    throw new Error("Failed to authenticate with Google");
  },

  // Helper method to process Google user data
  processGoogleUser: async (
    email: string,
    name?: string,
    picture?: string,
    sub?: string,
  ): Promise<User> => {
    console.log("processGoogleUser - Starting with email:", email);
    // Admin role is determined by backend user.role from storage

    try {
      // Create or update user via API
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: "POST",
        headers: getJsonCsrfHeaders(),
        body: JSON.stringify({
          id: `google_${sub || Date.now()}`,
          email,
          name: name || "Google User",
          picture:
            picture ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "Google User")}&background=random`,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to create/update user: ${response.status} ${response.statusText}`,
        );
      }

      const user = await response.json();
      console.log("processGoogleUser - User saved:", user);

      // Set as current user
      storage.setItem("auth_user", JSON.stringify(user));
      localStorage.setItem(storageKey, JSON.stringify(user));

      return user;
    } catch (error) {
      console.error("processGoogleUser - Error saving user:", error);
      throw error;
    }
  },

  // Sign out user
  signOut: async (): Promise<void> => {
    const user = getCurrentUser();
    if (user) {
      await notifyBackendLogout(user.email);
    }

    storage.removeItem("auth_user");
    localStorage.removeItem(storageKey);
  },

  // Save a new user or update an existing user
  createUser: async (newUser: {
    name: string;
    email: string;
    role: "user" | "admin";
  }): Promise<User> => {
    try {
      const userToAdd = {
        ...newUser,
        id: `user_${Date.now()}`,
        picture: `https://ui-avatars.com/api/?name=${encodeURIComponent(newUser.name)}&background=random`,
        lastLogin: new Date().toISOString(),
      };

      // Try to save to API only
      console.log("Saving user to API:", userToAdd);
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(userToAdd),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      console.log("User saved to API successfully");

      // Return the user data returned from the API
      return await response.json();
    } catch (error) {
      console.error("Error saving user to API:", error);
      // No localStorage fallback, just throw the error
      throw error;
    }
  },

  // Update a user's role
  updateUserRole: async (
    userId: string,
    role: "user" | "admin",
  ): Promise<void> => {
    try {
      // Allow in dev mode or demo mode without login
      if (process.env.NODE_ENV === "development" || DEMO_RUN) {
        // Directly call the backend API or update file as needed
        // (existing logic continues below)
      } else {
        // Get the current user to use for authentication
        const currentUser = authService.getCurrentUser();
        if (!currentUser) {
          throw new Error("You must be logged in to update user roles");
        }
      }

      // Check if we're offline
      if (!navigator.onLine) {
        console.log(
          "Device is offline. Switching to offline mode for role updates.",
        );

        // Update local storage in offline mode
        const users = JSON.parse(storage.getItem("auth_users") || "[]");
        const updatedUsers = users.map((u) =>
          u && u.id === userId
            ? { ...u, role, _originalRole: u.role, _offlineUpdated: true }
            : u,
        );

        storage.setItem("auth_users", JSON.stringify(updatedUsers));

        // Also update the current user in localStorage if it's the same user
        const storedUser = JSON.parse(storage.getItem("auth_user") || "null");
        if (storedUser && storedUser.id === userId) {
          storedUser.role = role;
          storedUser._originalRole = storedUser.role;
          storedUser._offlineUpdated = true;
          storage.setItem("auth_user", JSON.stringify(storedUser));
          localStorage.setItem(storageKey, JSON.stringify(storedUser));
          console.log(
            "Updated current user in localStorage with new role:",
            role,
          );
        }

        // Also update the credentials
        const credentials = storage.getItem("auth_credentials");
        if (credentials) {
          const parsedCredentials = JSON.parse(credentials);
          const updatedCredentials = parsedCredentials.map(
            (cred: UserCredential) =>
              cred.id === userId ? { ...cred, role } : cred,
          );
          storage.setItem(
            "auth_credentials",
            JSON.stringify(updatedCredentials),
          );
        }

        // Store pending change for later sync
        storePendingRoleChange(userId, role);

        console.log(
          "Role updated in offline mode. Changes will sync when back online.",
        );
        return;
      }

      // Try to update role using API first
      console.log(`Updating user ${userId} role to ${role} via API`);

      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), 5000);
      });

      // Create fetch promise
      const fetchPromise = fetch(`${API_BASE_URL}/users/${userId}/role`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ role }),
      });

      // Race the fetch against a timeout
      const response = (await Promise.race([
        fetchPromise,
        timeoutPromise,
      ])) as Response;

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      console.log(`User role updated to ${role} via API successfully`);

      // Get the updated user data from the response
      const updatedUser = await response.json();
      console.log("Updated user data received:", updatedUser.user);

      // Update local storage as well
      const users = await authService.getUsers();
      const updatedUsers = users.map((u) =>
        u && u.id === userId ? { ...u, role } : u,
      );

      storage.setItem("auth_users", JSON.stringify(updatedUsers));

      // Also update the current user in localStorage if it's the same user
      const storedUser = JSON.parse(storage.getItem("auth_user") || "null");
      if (storedUser && storedUser.id === userId) {
        storedUser.role = role;
        storage.setItem("auth_user", JSON.stringify(storedUser));
        localStorage.setItem(storageKey, JSON.stringify(storedUser));
        console.log(
          "Updated current user in localStorage with new role:",
          role,
        );
      }

      // Also update the credentials
      const credentials = storage.getItem("auth_credentials");
      if (credentials) {
        const parsedCredentials = JSON.parse(credentials);
        const updatedCredentials = parsedCredentials.map(
          (cred: UserCredential) =>
            cred.id === userId ? { ...cred, role } : cred,
        );
        storage.setItem("auth_credentials", JSON.stringify(updatedCredentials));
      }
    } catch (error) {
      console.error("Error updating user role:", error);

      // If there's a network error, fall back to local storage updates
      if (
        (error instanceof TypeError && error.message === "Failed to fetch") ||
        error.message === "Request timeout" ||
        !navigator.onLine
      ) {
        console.log(
          "Network error detected. Falling back to offline mode for role updates.",
        );

        // Update local storage in offline mode
        const users = JSON.parse(storage.getItem("auth_users") || "[]");
        const updatedUsers = users.map((u) =>
          u && u.id === userId
            ? { ...u, role, _originalRole: u.role, _offlineUpdated: true }
            : u,
        );

        storage.setItem("auth_users", JSON.stringify(updatedUsers));

        // Also update the current user in localStorage if it's the same user
        const storedUser = JSON.parse(storage.getItem("auth_user") || "null");
        if (storedUser && storedUser.id === userId) {
          storedUser.role = role;
          storedUser._originalRole = storedUser.role;
          storedUser._offlineUpdated = true;
          storage.setItem("auth_user", JSON.stringify(storedUser));
          localStorage.setItem(storageKey, JSON.stringify(storedUser));
          console.log(
            "Updated current user in localStorage with new role:",
            role,
          );
        }

        // Also update the credentials
        const credentials = storage.getItem("auth_credentials");
        if (credentials) {
          const parsedCredentials = JSON.parse(credentials);
          const updatedCredentials = parsedCredentials.map(
            (cred: UserCredential) =>
              cred.id === userId ? { ...cred, role } : cred,
          );
          storage.setItem(
            "auth_credentials",
            JSON.stringify(updatedCredentials),
          );
        }

        // Store pending change for later sync
        storePendingRoleChange(userId, role);

        console.log(
          "Role updated in offline mode. Changes will sync when back online.",
        );
        return;
      }

      throw error;
    }
  },

  // Simulate email/password sign in
  signInWithEmailAndPassword: async (
    email: string,
    password: string,
  ): Promise<User> => {
    // Only allow email/password login if DEMO_RUN is true
    if (!DEMO_RUN) {
      throw new Error("Email/password login is only available in demo mode");
    }

    const credentials = JSON.parse(storage.getItem("auth_credentials") || "[]");
    const userCredential = credentials.find(
      (cred: UserCredential) =>
        cred.email === email && cred.password === password,
    );

    if (!userCredential) {
      throw new Error("Invalid email or password");
    }

    try {
      // Get users list
      const users = await authService.getUsers();
      const user = users.find((u) => u && u.email === email);

      if (!user) {
        throw new Error("User not found");
      }

      const updatedUser = {
        ...user,
        lastLogin: new Date().toISOString(),
      };

      storage.setItem("auth_user", JSON.stringify(updatedUser));
      localStorage.setItem(storageKey, JSON.stringify(updatedUser));

      // Notify backend
      await notifyBackendLogin(updatedUser);

      return updatedUser;
    } catch (error) {
      console.error("Error during email/password login:", error);
      throw error;
    }
  },

  // Refresh user data
  refreshUserData: async (userData: Partial<User>): Promise<User | null> => {
    try {
      const currentUser = getCurrentUser();
      if (!currentUser) return null;

      const updatedUser = {
        ...currentUser,
        ...userData,
        lastLogin: new Date().toISOString(),
      };

      localStorage.setItem(storageKey, JSON.stringify(updatedUser));
      storage.setItem("auth_user", JSON.stringify(updatedUser));

      // Notify backend about the user update
      await notifyBackendLogin(updatedUser);

      return updatedUser;
    } catch (error) {
      console.error("Error refreshing user data:", error);
      return null;
    }
  },

  // Get current user
  getCurrentUser,

  // Check if a user is admin

  // Sync pending role changes when back online
  syncPendingRoleChanges: async (): Promise<any[]> => {
    if (!navigator.onLine) {
      console.log("Cannot sync pending changes while offline");
      return;
    }

    try {
      const pendingChangesStr = localStorage.getItem("pending_role_changes");
      if (!pendingChangesStr) {
        console.log("No pending role changes to sync");
        return;
      }

      const pendingChanges = JSON.parse(pendingChangesStr);
      if (!pendingChanges.length) {
        console.log("No pending role changes to sync");
        return;
      }

      console.log(
        `Found ${pendingChanges.length} pending role changes to sync`,
      );

      // Process each pending change
      const results = [];
      for (const change of pendingChanges) {
        try {
          console.log(
            `Syncing role change: User ${change.userId} -> ${change.role}`,
          );
          await authService.updateUserRole(change.userId, change.role);
          results.push({ userId: change.userId, success: true });
        } catch (error) {
          console.error(
            `Failed to sync role change for user ${change.userId}:`,
            error,
          );
          results.push({ userId: change.userId, success: false, error });
        }
      }

      // Clear the pending changes that were successful
      const successfulIds = results
        .filter((r) => r.success)
        .map((r) => r.userId);
      const remainingChanges = pendingChanges.filter(
        (change) => !successfulIds.includes(change.userId),
      );

      localStorage.setItem(
        "pending_role_changes",
        JSON.stringify(remainingChanges),
      );

      console.log(
        `Synced ${successfulIds.length} of ${pendingChanges.length} pending role changes`,
      );
      return results;
    } catch (error) {
      console.error("Error syncing pending role changes:", error);
      return [];
    }
  },

  async login(email: string, password: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: getJsonCsrfHeaders(),
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error("Login failed");
      }

      const data = await response.json();
      storage.setItem("auth_user", JSON.stringify(data.user));
      return data;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  },

  async logout() {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: "POST",
        headers: getJsonCsrfHeaders(),
      });
      storage.removeItem("auth_user");
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  },
};
